import * as React from 'react';
import { produce } from 'immer';
import { createContainer } from 'react-tracked';

export const PLAYER_COMMANDS = {
  LOAD: 'LOAD',
  PLAY: 'PLAY',
  PAUSE: 'PAUSE',
} as const;

export type PlayerCommands = (typeof PLAYER_COMMANDS)[keyof typeof PLAYER_COMMANDS];

export const PLAYER_STATES = {
  LOADING: 'LOADING',
  PAUSED: 'PAUSED',
  PLAYING: 'PLAYING',
} as const;

export type PlayerStates = (typeof PLAYER_STATES)[keyof typeof PLAYER_STATES];

enum PLAYER_ACTION_TYPES {
  SET_PLAYER_STATE,
  SET_PLAYER_DETAILS,
}

type PlayerDetails = {
  duration: string;
  playerKey: number;
  videoContainer: HTMLElement | null;
};

export type PlayerDetailsAction = Omit<Partial<PlayerDetails>, 'duration'> & { duration: number };

type LoadingPlayerState = {
  duration?: string;
  playerKey: number;
  playerState: 'loading';
  videoContainer?: HTMLElement | null;
};

type ActivePlayerState = PlayerDetails & {
  playerState: PlayerStates;
};

export type PlayerState = ActivePlayerState | LoadingPlayerState;

export type PlayerContextHook = PlayerState & {
  playerCommands: typeof PLAYER_COMMANDS;
  playerStates: typeof PLAYER_STATES;
  setPlayerDetails: (v: PlayerDetailsAction) => void;
  setPlayerState: (v: PlayerCommands) => void;
};

type PlayerAction =
  | {
      payload: {
        duration: number;
        videoContainer?: HTMLElement | null;
      };
      type: PLAYER_ACTION_TYPES.SET_PLAYER_DETAILS;
    }
  | {
      payload: PlayerCommands;
      type: PLAYER_ACTION_TYPES.SET_PLAYER_STATE;
    };

function formatDuration(durationInSeconds: number) {
  const hours = Math.floor(durationInSeconds / 60 / 60);
  const minutes = Math.round(durationInSeconds / 60) - hours * 60;
  return `${hours ? `${hours}h ` : ''}${minutes}m`;
}

const videoPlayerReducer = (state: PlayerState, action: PlayerAction) => {
  switch (action.type) {
    case PLAYER_ACTION_TYPES.SET_PLAYER_STATE:
      if (action.payload === PLAYER_COMMANDS.LOAD) {
        state.playerState = PLAYER_STATES.LOADING;
        state.playerKey = state.playerKey + 1;
      } else if (action.payload === PLAYER_COMMANDS.PAUSE) {
        state.playerState = PLAYER_STATES.PAUSED;
      } else {
        state.playerState = PLAYER_STATES.PLAYING;
      }
      break;
    case PLAYER_ACTION_TYPES.SET_PLAYER_DETAILS:
      state.duration = formatDuration(action.payload.duration);
      state.videoContainer = action.payload.videoContainer;
      state.playerState = PLAYER_STATES.PLAYING;
      break;
  }
};

const curriedReducerFunction = produce(videoPlayerReducer);

const useValues = (): readonly [PlayerContextHook, () => void] => {
  const [state, dispatch] = React.useReducer(curriedReducerFunction, {
    duration: '',
    playerState: PLAYER_STATES.LOADING,
    playerKey: 0,
    videoContainer: null,
  });

  const setPlayerState = React.useCallback(
    (playerCommand: PlayerCommands) =>
      dispatch({
        type: PLAYER_ACTION_TYPES.SET_PLAYER_STATE,
        payload: playerCommand,
      }),
    []
  );

  const setPlayerDetails = React.useCallback((details: PlayerDetailsAction) => {
    dispatch({
      type: PLAYER_ACTION_TYPES.SET_PLAYER_DETAILS,
      payload: details,
    });
  }, []);

  const stateToPass = React.useMemo(
    () => ({
      ...state,
      playerCommands: PLAYER_COMMANDS,
      playerStates: PLAYER_STATES,
      setPlayerState,
      setPlayerDetails,
    }),
    [setPlayerDetails, setPlayerState, state]
  );

  return [
    stateToPass,
    () => {
      throw new Error('use functions in the state');
    },
  ];
};

const {
  Provider: PlayerContextProvider,
  useTrackedState: usePlayerContext,
  useUpdate: usePlayerContextUpdate,
} = createContainer<PlayerContextHook, () => void, { children: React.ReactNode }>(useValues);

export { PlayerContextProvider, usePlayerContext, usePlayerContextUpdate };
